import React from 'react';
import { compose } from 'redux';
import { PropTypes } from 'prop-types';
import { upperFirst } from 'lodash';
import { withStyles } from '@material-ui/styles';
import DoneIcon from '@material-ui/icons/Done';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import ClearIcon from '@material-ui/icons/Clear';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { connect } from 'react-redux';
import moment from 'moment';
import SequenceBuilderActions from './SequenceBuilderActions';
import { frenchPink } from '../../../themes/colors';
import { WEEK_DAYS } from '../../../constants/config';
import { ADD_SEQUENCE_STEPS } from '../../../constants/sequence';
import Button from '../../buttons/Button';
import { getDiffInMinutes } from '../../../utils/misc';

const styles = theme => ({
  summaryRow: {
    color: theme.palette.primary.text,
  },
  summaryTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  },
  titleIcon: {
    marginRight: '8px',
    padding: '3px 4px',
    borderRadius: '50%',
    '& svg': {
      fontSize: '1rem',
    },
    '&.complete': {
      backgroundColor: fade(theme.palette.primary.main, 0.2),
      '& svg': {
        color: theme.palette.primary.main,
      },
    },
    '&.incomplete': {
      backgroundColor: fade(frenchPink, 0.2),
      '& svg': {
        color: frenchPink,
      },
    },
  },
  summaryDescription: {
    letterSpacing: '-0.2px',
    marginTop: '1rem',
    lineHeight: '23px',
    '& strong': {
      fontWeight: 600,
    },
    '& button': {
      marginTop: '1rem',
      display: 'block',
    },
  },
});

const getScheduleString = schedule => (
  WEEK_DAYS.slice(1, 6).every(day => schedule[day]) ? 'All day Mon - Fri' :
    WEEK_DAYS.filter(day => schedule[day]).map(i => upperFirst(i.substring(0, 3))).join(' ')
);

const getContactsString = (sequence, contactCount) => (
  sequence.validate_contacts ?
    `Email validation will run on ${contactCount} contact(s)
     and send to valid ${sequence.send_to_risky ? 'and risky' : ''} contacts.` :
    `The pitches will be sent to ${contactCount} contact(s). Email validation will not run.`
);

const getFormattedLaunchDate = (sequence, schedule) => {
  const timeDiff = getDiffInMinutes(schedule.timezone);
  return moment(sequence.launch_date).add(-timeDiff, 'minutes').format('LLL');
};

const SequenceSummary = ({
  classes,
  onBack,
  onDelete,
  onSave,
  onNext,
  sequence,
  schedule,
  emailConfigs,
  contactsCount,
  stepsCount,
  setStep,
}) => (
  <div className="m-3">
    <div className={classes.summaryRow}>
      <h6 className={classes.summaryTitle}>
        {contactsCount !== null ?
          <span className={`${classes.titleIcon} complete`} >
            <DoneIcon />
          </span> :
          <span className={`${classes.titleIcon} incomplete`}>
            <ClearIcon />
          </span>
        }
        <span>Contacts</span>
      </h6>
      <p className={classes.summaryDescription}>
        {contactsCount !== null ?
          getContactsString(sequence, contactsCount) :
          <>
            Add contacts to launch the sequence
            <Button
              type="button"
              color="secondary"
              variant="textPrimaryContained"
              onClick={() => setStep(ADD_SEQUENCE_STEPS.CONTACTS_START_STEP)}
              startIcon={<AddCircleOutlineIcon />}
            >
              Add Contacts
            </Button>
          </>
        }
      </p>
    </div>
    <hr />
    <div className={classes.summaryRow}>
      <h6 className={classes.summaryTitle}>
        {stepsCount ?
          <span className={`${classes.titleIcon} complete`} >
            <DoneIcon />
          </span> :
          <span className={`${classes.titleIcon} incomplete`}>
            <ClearIcon />
          </span>
        }
        <span>Pitches</span>
      </h6>
      <p className={classes.summaryDescription}>
        {stepsCount ?
          `Your sequence has a total of ${stepsCount} step(s).` :
          <>
            Add steps to launch the sequence
            <Button
              type="button"
              color="secondary"
              variant="textPrimaryContained"
              onClick={() => setStep(ADD_SEQUENCE_STEPS.PITCHES_STEP)}
              startIcon={<AddCircleOutlineIcon />}
            >
              Add Steps
            </Button>
          </>
        }
      </p>
    </div>
    <hr />
    {schedule &&
      <div className={classes.summaryRow}>
        <h6 className={classes.summaryTitle}>
          <span className={`${classes.titleIcon} complete`}>
            <DoneIcon color="primary" />
          </span>
          <span>Schedule</span>
        </h6>
        <p className={classes.summaryDescription}>
          Your sequence <strong>{sequence.name}</strong> will launch on
          <strong>
            &nbsp;{getFormattedLaunchDate(sequence, schedule)}{schedule.timezone && ` - ${schedule.timezone}`}&nbsp;
          </strong>
          and send according to the schedule <strong>{getScheduleString(schedule)}</strong>.
        </p>
      </div>
    }
    <hr />
    <div className={classes.summaryRow}>
      <h6 className={classes.summaryTitle}>
        <span className={`${classes.titleIcon} complete`}>
          <DoneIcon color="primary" />
        </span>
        <span>Sending settings</span>
      </h6>
      <p className={classes.summaryDescription}>
        <strong className="block">Pitches will be sent from:</strong>
        {emailConfigs.map(emailConfig => (
          <div>
            <strong>- {emailConfig.sender_name} ({emailConfig.email})</strong>.
            The email address will send a maximum of <strong>{emailConfig.total_number_of_emails_per_day} </strong>
            emails per day, spaced out by <strong>{emailConfig.delay_between_each_email_sent} </strong>seconds.
          </div>
        ))}
      </p>
      <p>
        <i>
          Need to change sending settings? Click “save & don’t launch”, then navigate to campaign settings.
        </i>
      </p>
    </div>
    <SequenceBuilderActions
      onBack={onBack}
      onDelete={onDelete}
      onSave={onSave}
      onNext={onNext}
      saveBtnText="Save & Don't Launch"
      nextBtnText="Save & Launch"
      disabledNext={!stepsCount || !contactsCount}
    />
  </div>
);


SequenceSummary.propTypes = {
  classes: PropTypes.string.isRequired,
  onSave: PropTypes.string.isRequired,
  onBack: PropTypes.string.isRequired,
  onNext: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  sequence: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  emailConfigs: PropTypes.arrayOf(PropTypes.shape({
    email: PropTypes.string,
    sender_name: PropTypes.string,
  })).isRequired,
  campaign: PropTypes.shape({
    total_number_of_emails_per_day: PropTypes.number,
    delay_between_each_email_sent: PropTypes.number,
  }).isRequired,
  schedule: PropTypes.shape({}).isRequired,
  contactsCount: PropTypes.number.isRequired,
  stepsCount: PropTypes.number.isRequired,
  setStep: PropTypes.func.isRequired,
};

export default compose(
  withStyles(styles),
  connect(state => ({
    sequence: state.sequence,
    emailConfigs: state.campaign.email_service_provider_configs,
    contactsCount: state.previewContacts.total_valid_contacts,
    stepsCount: state.steps.results.length,
    schedule: state.schedules.results.find(seq => seq.id === state.sequence.schedule),
  })),
)(SequenceSummary);
